// v1.3.10 — the keys of the wallet, as Arc holds them.
//
// The wallet is a Circle modular account. Who may sign for it is kept by one
// module, Circle's weighted multisig plugin, at a fixed address. M's passkey
// is one entry there (weight 1, threshold 1). A spare key is a second entry:
// an ordinary address with the same weight, so either one signs alone.
//
// Read from Circle's published source (circlefin/buidl-wallet-contracts,
// WeightedWebauthnMultisigPlugin v1.0.0):
//   · addOwners / removeOwners run on the wallet itself (the plugin installs
//     them as wallet functions). They pass ONLY as a user operation through
//     the EntryPoint — the plugin's direct-call check always reverts — and
//     they cannot ride inside executeBatch, for the same reason. So adding or
//     deleting a key is its own user operation whose callData is the call
//     itself, not wrapped in execute().
//   · isOwnerOf / ownershipInfoOf are plain reads on the plugin.
//
// This file is light (ABI + encoders + reads): safe in the main bundle.

import { encodeFunctionData, getAddress, type Hex } from "viem";

/** ERC-4337 EntryPoint v0.7 — the same address on every chain. */
export const ENTRY_POINT = "0x0000000071727De22E5E9d8BAf0edAc6f37da032" as const;
/** Circle Weighted WebAuthn Multisig Plugin v1.0.0 — the same address on every chain. */
export const OWNERS_PLUGIN = "0x0000000C984AFf541D6cE86Bb697e68ec57873C8" as const;

const PUBLIC_KEY = { type: "tuple[]", components: [{ name: "x", type: "uint256" }, { name: "y", type: "uint256" }] } as const;
const OWNER_DATA = [
  { name: "weight", type: "uint256" },
  { name: "credType", type: "uint8" },
  { name: "addr", type: "address" },
  { name: "publicKeyX", type: "uint256" },
  { name: "publicKeyY", type: "uint256" },
] as const;

export const OWNERS_ABI = [
  {
    type: "function", name: "addOwners", stateMutability: "nonpayable", outputs: [],
    inputs: [
      { name: "ownersToAdd", type: "address[]" },
      { name: "weightsToAdd", type: "uint256[]" },
      { name: "publicKeyOwnersToAdd", ...PUBLIC_KEY },
      { name: "pubicKeyWeightsToAdd", type: "uint256[]" },
      { name: "newThresholdWeight", type: "uint256" },
    ],
  },
  {
    type: "function", name: "removeOwners", stateMutability: "nonpayable", outputs: [],
    inputs: [
      { name: "ownersToRemove", type: "address[]" },
      { name: "publicKeyOwnersToRemove", ...PUBLIC_KEY },
      { name: "newThresholdWeight", type: "uint256" },
    ],
  },
  {
    type: "function", name: "isOwnerOf", stateMutability: "view",
    inputs: [{ name: "account", type: "address" }, { name: "ownerToCheck", type: "address" }],
    outputs: [{ name: "", type: "bool" }, { name: "", type: "tuple", components: OWNER_DATA }],
  },
  {
    type: "function", name: "ownershipInfoOf", stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [
      { name: "ownerAddresses", type: "bytes30[]" },
      { name: "ownersData", type: "tuple[]", components: OWNER_DATA },
      { name: "ownershipMetadata", type: "tuple", components: [
        { name: "numOwners", type: "uint256" },
        { name: "thresholdWeight", type: "uint256" },
        { name: "totalWeight", type: "uint256" },
      ] },
    ],
  },
] as const;

/** callData of the user operation that adds one ordinary key. Threshold 0 = leave it as it is. */
export function addKeyData(key: string, weight: bigint = 1n): Hex {
  return encodeFunctionData({
    abi: OWNERS_ABI,
    functionName: "addOwners",
    args: [[getAddress(key)], [weight], [], [], 0n],
  });
}

/** callData of the user operation that deletes one ordinary key (the plugin calls it removeOwners). */
export function deleteKeyData(key: string): Hex {
  return encodeFunctionData({
    abi: OWNERS_ABI,
    functionName: "removeOwners",
    args: [[getAddress(key)], [], 0n],
  });
}

/** The one read this file needs; viem's PublicClient satisfies it. */
export interface ContractReader {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readContract(args: any): Promise<unknown>;
}

export interface WalletKey {
  /** "key" — an ordinary key (a spare key). "passkey" — Face ID / fingerprint on a phone. */
  kind: "key" | "passkey";
  /** Set for an ordinary key. */
  address?: Hex;
  weight: bigint;
}
export interface WalletKeys {
  keys: WalletKey[];
  /** Weight needed to sign. 1 in every wallet M makes. */
  threshold: bigint;
}

interface OwnerData { weight: bigint; credType: number; addr: Hex; publicKeyX: bigint; publicKeyY: bigint }

/** Every key on the wallet, read from Arc. An undeployed wallet reads as no keys. */
export async function readKeys(client: ContractReader, wallet: string): Promise<WalletKeys> {
  const [, data, meta] = (await client.readContract({
    address: OWNERS_PLUGIN,
    abi: OWNERS_ABI,
    functionName: "ownershipInfoOf",
    args: [getAddress(wallet)],
  })) as [readonly Hex[], readonly OwnerData[], { numOwners: bigint; thresholdWeight: bigint; totalWeight: bigint }];
  return {
    // credType (CommonStructs.sol CredentialType): 0 = a passkey's public key, 1 = an address.
    keys: data.map((d) => (d.credType === 1
      ? { kind: "key" as const, address: getAddress(d.addr), weight: d.weight }
      : { kind: "passkey" as const, weight: d.weight })),
    threshold: meta.thresholdWeight,
  };
}

/** Is this ordinary key on the wallet, and can it sign alone? */
export async function readKey(client: ContractReader, wallet: string, key: string): Promise<{ on: boolean; alone: boolean; weight: bigint; threshold: bigint }> {
  const [on, data] = (await client.readContract({
    address: OWNERS_PLUGIN,
    abi: OWNERS_ABI,
    functionName: "isOwnerOf",
    args: [getAddress(wallet), getAddress(key)],
  })) as [boolean, OwnerData];
  if (!on) return { on: false, alone: false, weight: 0n, threshold: 0n };
  const { threshold } = await readKeys(client, wallet);
  return { on: true, alone: data.weight >= threshold, weight: data.weight, threshold };
}
